import React from 'react';

import { Box, Button } from '@mui/material';

type Props = {
  onViewMore: () => void;
  lineCount: number;
};

const NoteText: React.FC<Props> = ({ children, onViewMore, lineCount }) => {
  const divRef = React.useRef<HTMLElement>();
  const [overflowActive, setOverflowActive] = React.useState(false);

  const checkOverflow = (textContainer: HTMLElement | undefined) => {
    if (textContainer) {
      return (
        textContainer.offsetHeight < textContainer.scrollHeight ||
        textContainer.offsetWidth < textContainer.scrollWidth
      );
    }
    return false;
  };

  const handleResize = () => {
    checkOverflow(divRef.current)
      ? setOverflowActive(true)
      : setOverflowActive(false);
  };

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleResize();
          }
        });
      },
      {
        root: null,
        threshold: 0,
      }
    );

    if (divRef.current) {
      observer.observe(divRef.current);
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  React.useEffect(() => {
    handleResize();
  }, [children]);

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        ref={divRef}
        sx={{
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: `${lineCount}`,
          WebkitBoxOrient: 'vertical',
          width: '100%',
          overflow: 'hidden',
          flexDirection: 'row',
          textAlign: 'left',
        }}
      >
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            flexGrow: 1,
            position: 'relative',
            fontFamily: 'SFProDisplay-Medium',
            color: '#ffffff',
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            marginTop: 4,
            marginBottom: 4,
            marginRight: 0,
            marginLeft: 0,
            height: '100%',
          }}
          className='small-font-on-mobile'
        >
          {children}
        </pre>
      </Box>
      {overflowActive && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            paddingTop: '4px',
          }}
        >
          <Button
            onClick={onViewMore}
            variant='text'
            className='show-more-button'
            color='primary'
            sx={{
              color: '#266071',
            }}
          >
            Show more
          </Button>
        </div>
      )}
    </Box>
  );
};

export default NoteText;
